// jQuery를 Summernote보다 먼저 전역 등록 (최우선 import)
import '@/plugins/jquery-global';

import { createApp } from 'vue';
import * as Sentry from '@sentry/vue';

import App from '@/App.vue';
import { registerPlugins } from '@core/utils/plugins';

import { setupCalendar } from 'v-calendar';
import 'v-calendar/style.css';
import VCalendar from 'v-calendar';
// Styles
import '@core/scss/template/index.scss';
import '@layouts/styles/index.scss';
import '@/assets/styles/global.scss';
import '@/assets/styles/tailwind.css';
import vue3GoogleLogin from 'vue3-google-login';
import { useUserStore } from '@/stores/userStore';
import { isInAppBrowser } from '@/utils/inAppBrowser';

// 테스트 사이트 여부 플래그 (DEV 서버 또는 env 플래그)
const isTestSite = import.meta.env.VITE_TEST_SITE === 'true' || import.meta.env.DEV;

// 테스트 사이트에서는 브라우저 탭 제목과 파비콘에 [테스트] 표시
if (isTestSite) {
  document.title = '[테스트] 마이정토';

  const existingFavicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");

  if (existingFavicon) {
    existingFavicon.href = '/favicon_dev.png';
  } else {
    const newFavicon = document.createElement('link');
    newFavicon.rel = 'icon';
    newFavicon.href = '/favicon_dev.png';
    document.head.appendChild(newFavicon);
  }
}

// Create vue app
const app = createApp(App);

// ============================================
// Sentry 초기화 (환경별 DSN 분기)
// ============================================
// 운영/개발 서버가 각각 다른 Sentry 프로젝트로 전송되도록 DSN을 분기한다.
// - 개발 배포: `pnpm run build:dev` → import.meta.env.MODE === 'development'
// - 운영 배포: `pnpm run build:prod` → import.meta.env.MODE === 'production'
// 환경 판별 우선순위: VITE_SENTRY_ENVIRONMENT > MODE > 'development'
// DSN 우선순위: VITE_SENTRY_DSN(직접 지정) > 환경별 자동 선택
const SENTRY_DSN_PRODUCTION = 'https://f46267ea455a727a554f6f6adb175031@o4511920284041216.ingest.us.sentry.io/4512042771415040';
const SENTRY_DSN_DEVELOPMENT = 'https://d73b1ff6a45d524dea17abb0bc38a914@o4511920284041216.ingest.us.sentry.io/4512042764468224';

const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const isSentryProduction = sentryEnvironment === 'production';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || (isSentryProduction ? SENTRY_DSN_PRODUCTION : SENTRY_DSN_DEVELOPMENT);

// 릴리스 식별자 (CI에서 VITE_SENTRY_RELEASE 로 주입 — dev는 커밋 SHA, 운영은 태그)
// Sentry 릴리스에 커밋을 연결해 Suspect Commits(범인 커밋 자동 지목)를 활성화한다.
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE || undefined;

// Session Replay 샘플레이트 (환경별 분기, DSN과 동일한 isSentryProduction 기준 사용)
// - 운영: 일반 세션은 10%만 녹화(비용·개인정보 노출 최소화), 에러가 발생한 세션은 100% 녹화
// - 개발: 개발 중 재현 확인을 위해 모든 세션 100% 녹화
const replaysSessionSampleRate = isSentryProduction ? 0.1 : 1.0;
const replaysOnErrorSampleRate = 1.0;

Sentry.init({
  app,
  dsn: sentryDsn,
  environment: sentryEnvironment,
  release: sentryRelease,
  // 인앱 브라우저(카카오톡 등)에서 구글 GSI 스크립트 로드가 차단되어 발생하는
  // "Failed to load the Google 3P Authorization JavaScript Library." 는
  // 코드로 해결 불가능한 정책 차단이며 외부 브라우저 유도 UI 로 대응한다.
  // 노이즈 알림을 막기 위해 Sentry 전송에서 제외한다. (안전망)
  ignoreErrors: [/Failed to load the Google 3P Authorization JavaScript Library/],
  integrations: [
    // Session Replay.
    // ⚠️ 현재 정책: 마스킹 전면 해제 — 모든 텍스트·미디어·입력값이 그대로 녹화된다.
    //    마이정토는 회원정보·삼보수호비(후원금) 등 민감정보를 다루므로, 운영 배포 전
    //    개인정보 노출 범위를 반드시 재검토할 것. (dev만 노출하려면
    //    maskAllText: isSentryProduction 형태로 기존 환경 분기에 얹으면 됨)
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      maskAllInputs: false,
    }),
  ],
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
});

// eslint-disable-next-line no-console
console.log(`🛰️ Sentry 초기화 완료 → environment=${sentryEnvironment}, project=${isSentryProduction ? 'PRODUCTION' : 'DEVELOPMENT'}, release=${sentryRelease ?? 'none'}`);

// Register plugins
registerPlugins(app);

// Use user store after plugins are registered (persistedstate로 자동 복원)
const userStore = useUserStore();

// 앱 시작 시 자동 로그인 체크
(async () => {
  // Refresh token이 있고 유효한 경우 자동 로그인 시도
  if (userStore.refreshToken && !userStore.isRefreshTokenExpired()) {
    //console.log('🔄 [App Init] Refresh token이 유효함, 자동 로그인 시도');
    try {
      const refreshSuccess = await userStore.refreshAccessToken();
      if (refreshSuccess) {
        //console.log('✅ [App Init] 자동 로그인 성공');
        userStore.setIsLogin(true);
      } else {
        //console.log('❌ [App Init] 자동 로그인 실패');
      }
    } catch (error) {
      console.error('❌ [App Init] 자동 로그인 중 오류:', error);
    }
  } else if (userStore.refreshToken && userStore.isRefreshTokenExpired()) {
    //console.log('⏰ [App Init] Refresh token이 만료됨, 저장된 데이터 정리');
    userStore.clearStore();
  }
})();

// VCalendar 설정
app.use(setupCalendar, {
  // locale: 'ko-KR',
  firstDayOfWeek: 0, // 0: 일요일, 1: 월요일
});

app.use(VCalendar, {});

// 인앱 브라우저(WebView)에서는 구글 GSI 스크립트 로드가 차단되어
// vue3-google-login 플러그인 install 내부의 스크립트 로드가 unhandled rejection
// ("Failed to load the Google 3P Authorization JavaScript Library.")을 발생시킨다.
// 어차피 인앱 브라우저에서는 구글 OAuth 가 불가능하고, 로그인 화면에서 외부 브라우저
// 유도 UI 를 노출하므로, 인앱 브라우저에서는 플러그인 등록(=스크립트 로드) 자체를 건너뛴다.
if (!isInAppBrowser()) {
  app.use(vue3GoogleLogin, {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
  });
}

// Mount vue app
app.mount('#app');
